import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Paper, Stack, Typography, Avatar, Chip, Grid, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar } from '@mui/material';
import axios from 'axios';
import ProfilePictureUpload from './ProfilePictureUpload';
import AddFamilyMember from './AddFamilyMember';
import AddJobDetail from './AddJobDetail';
import AddEducationDetail from './AddEducationDetail';
import Address from './Address';
import API_BASE_URL, { BASE_URL } from '../config';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import { Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomFieldComponent from './CustomField';
import DownloadIcon from '@mui/icons-material/Download';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import CakeIcon from '@mui/icons-material/Cake';
import { List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import UpgradeModal from '../components/UpgradeModal';
import useAccess from '../hooks/useAccess';
import { getErrorMessage } from '../utils/apiError';

// Fields that count toward the free-plan edit limit
const COUNTED_FIELDS = ['prefix','first_name','middle_name','last_name','display_name','phone_number','email_id','address','area','district','state','village_name','pincode','dob','gender'];
const FREE_EDIT_LIMIT = 3;

const getImageUrl = (path) => {
  if (!path) return '/goldpetals/uploads/default-profile.png';
  if (path.startsWith('blob:')) return path;
  if (path.startsWith('http') && !path.includes('localhost')) return path;
  if (path.startsWith('http') && path.includes('localhost')) {
    return path.replace(/http:\/\/localhost:\d+/, BASE_URL);
  }
  if (path.startsWith('/')) return `${BASE_URL}${path}`;
  return `${BASE_URL}/uploads/${path}`;
};

const UserCardForDownload = React.forwardRef(({ userData }, ref) => {
  const isFieldEmpty = (value) => !value || value === '';

  return (
    <Box
      ref={ref}
      sx={{
        p: 4,
        bgcolor: 'white',
        borderRadius: 2,
        width: '100%',
        maxWidth: '600px',
        display: 'none' // Hidden by default, only used for PDF generation
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'primary.main',
            fontSize: '2rem'
          }}
        >
          {userData.first_name ? userData.first_name[0] : ''}
        </Avatar>
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight="bold">
            {`${userData.prefix || ''} ${userData.first_name || ''} ${userData.middle_name || ''} ${userData.last_name || ''}`}
          </Typography>
          <Chip
            label={`UID: ${userData.uid}`}
            color="primary"
            size="small"
          />
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {/* Personal Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            Personal Information
          </Typography>
        </Grid>

        {!isFieldEmpty(userData.display_name) && (
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Display Name</Typography>
              <Typography variant="body2">{userData.display_name}</Typography>
            </Stack>
          </Grid>
        )}

        {!isFieldEmpty(userData.phone_number) && (
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Phone Number</Typography>
              <Typography variant="body2">{userData.phone_number}</Typography>
            </Stack>
          </Grid>
        )}

        {!isFieldEmpty(userData.email_id) && (
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body2">{userData.email_id}</Typography>
            </Stack>
          </Grid>
        )}

        {!isFieldEmpty(userData.dob) && (
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body2">{new Date(userData.dob).toLocaleDateString()}</Typography>
            </Stack>
          </Grid>
        )}

        {!isFieldEmpty(userData.gender) && (
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <Typography variant="body2">{userData.gender}</Typography>
            </Stack>
          </Grid>
        )}

        {/* Address Information */}
        {(userData.address || userData.village_name || userData.area || userData.district || userData.state) && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mt: 2 }} gutterBottom>
                Address Information
              </Typography>
            </Grid>

            {!isFieldEmpty(userData.address) && (
              <Grid item xs={12}>
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{userData.address}</Typography>
                </Stack>
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2">
                  {[
                    userData.village_name,
                    userData.area,
                    userData.district,
                    userData.state,
                    userData.pincode
                  ].filter(Boolean).join(', ')}
                </Typography>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>

      {/* QR Code */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <QRCodeCanvas
          value={userData.uid || ''}
          size={100}
          level="H"
          includeMargin={true}
        />
      </Box>
    </Box>
  );
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: theme.spacing(2),
  background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
}));

const WelcomeCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  borderRadius: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.03),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  position: 'relative',
  paddingLeft: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '60%',
    background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: theme.spacing(1),
  },
}));

const PersonalInfoCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const InfoTypeIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  marginRight: theme.spacing(2),
}));

const SuccessDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: (theme) => `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          backdropFilter: 'blur(10px)',
          boxShadow: (theme) => `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
        }
      }}
    >
      <DialogTitle sx={{
        background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: 'white',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        Success
      </DialogTitle>
      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.primary">
          Profile updated successfully!
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: 2,
            px: 4,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: (theme) => `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VisitorDialog = ({ open, onClose, visitors }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: (theme) => `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: 'white',
        fontWeight: 'bold'
      }}>
        Recent Profile Visitors
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {visitors.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No visitors yet.</Typography>
        ) : (
        <List>
          {visitors.map((visitor, index) => (
            <React.Fragment key={visitor.id || index}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar
                    src={getImageUrl(visitor.profile_image)}
                    alt={visitor.visitor_name || 'Visitor'}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={visitor.visitor_name || 'Unknown'}
                  secondary={
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Visited on: {new Date(visitor.visited_at).toLocaleDateString()} at{' '}
                        {new Date(visitor.visited_at).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItem>
              {index < visitors.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: (theme) => `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditProfile = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uidFromUrl = searchParams.get('uid');
  const navigate = useNavigate();
  const { isPremium, isAdmin, loading: accessLoading } = useAccess();
  const [userData, setUserData] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});
  const familyMemberRef = useRef(null);

  // CRITICAL UI logic update: shadow useAccess with local Database Truth for instant updates
  const isPremiumPlan = isAdmin || (userData?.plan_type === 'premium' && userData?.plan_status === 'active');
  const currentIsPremium = isPremium || isPremiumPlan;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    display_name: '',
    phone_number: '',
    email_id: '',
    address: '',
    area: '',
    district: '',
    state: '',
    village_name: '',
    pincode: '',
    dob: '',
    age: '',
    gender: '',
    role: 0,
    tree_id: '',
    uid: '',
    position: '',
  });
  const [addresses, setAddresses] = useState([]); // State for multiple addresses  setAllFamilyMembers
  const [familyMembers, setFamilyMembers] = useState([]);
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [editMode, setEditMode] = useState(false); // Add state for toggle
  const [educationDetails, setEducationDetails] = useState([]); // State for education details
  const [allJobDetails, setAllJobDetails] = useState([]);
  const [CustomField, setCustomField] = useState([]);
  const [treeId, setTreeId] = useState([]);
  const cardRef = React.useRef(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [hasVisitors, setHasVisitors] = useState(false);
  const [visitorDialogOpen, setVisitorDialogOpen] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

   const handleUpgradeSuccess = () => {
    setUserData(prev => prev ? {
      ...prev,
      plan_type: 'premium',
      plan_status: 'active',
    } : prev);
    setUpgradeModalOpen(false);
  };

  const fetchUserData = async () => {
  try {
    const userStatus = localStorage.getItem('userRole');
    setIsGuestUser(userStatus == '2');
    setLoading(true);

    // ✅ Single session call
    const sessionResponse = await axios.get(`${API_BASE_URL}/user/id`, {
      withCredentials: true
    });

    const userId = sessionResponse.data?.userId;

    if (!userId) {
      throw new Error('User not authenticated');
    }

    // ✅ Parallel API calls (optimized)
    const [
      userResponse,
      educationResponse,
      jobsResponse,
      addressResponse,
      customResponse
    ] = await Promise.all([
      axios.get(`${API_BASE_URL}/user/${userId}`, { withCredentials: true }),
      axios.get(`${API_BASE_URL}/education/${userId}`, { withCredentials: true })
        .catch(() => ({ data: { educationDetails: [] } })),
      axios.get(`${API_BASE_URL}/jobs/${userId}`, { withCredentials: true })
        .catch(() => ({ data: [] })),
      axios.get(`${API_BASE_URL}/addresses/${userId}`, { withCredentials: true })
        .catch(() => ({ data: { addresses: [] } })),
      axios.get(`${API_BASE_URL}/custom_fields/${userId}`, { withCredentials: true })
        .catch(() => ({ data: [] }))
    ]);

    if (!userResponse.data?.success) {
      throw new Error('Failed to fetch user data');
    }

    const user = userResponse.data.user;
    // FRONTEND NORMALIZATION
    user.plan_type = user.plan_type || "free";
    user.plan_status = user.plan_status || "inactive";

    setUserData(user);
    setTreeId(user.id);
    setFormData(prev => ({ ...prev, ...user }));

    // ✅ Fix profile image path safely
    const imageUrl = getImageUrl(user.image_url);
    setProfilePicUrl(imageUrl);

    // ✅ Education data
    if (educationResponse?.data?.educationDetails) {
      setEducationDetails(educationResponse.data.educationDetails);
    }

    // ✅ Jobs data
    if (Array.isArray(jobsResponse?.data)) {
      setAllJobDetails(jobsResponse.data);
    }

    // ✅ Addresses data
    if (addressResponse?.data?.addresses) {
      setAddresses(addressResponse.data.addresses);
    }

    // ✅ Custom fields data
    if (Array.isArray(customResponse?.data)) {
      setCustomField(customResponse.data);
    }

    // ✅ Fetch plan status (premium check) — now driven by useAccess hook above
    // setIsPremium is removed; isPremium comes from useAccess
    setOriginalFormData(user);

    setLoading(false);

  } catch (error) {
    console.error('Error fetching user data:', error);
    setError(getErrorMessage(error));
    setLoading(false);

    if (
      error.response?.status === 401 ||
      error.message === 'User not authenticated'
    ) {
      setTimeout(() => navigate('/login'), 2000);
    }
  }
};
  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (uidFromUrl && uidFromUrl !== localStorage.getItem('uid')) {
      setEditMode(false);
    }
  }, [uidFromUrl]);

  useEffect(() => {
    // Auto-trigger upgrade modal for free users immediately after login
    if (!accessLoading && !loading && !currentIsPremium && !isAdmin) {
        setUpgradeModalOpen(true);
    }
  }, [accessLoading, loading, currentIsPremium, isAdmin]);

  useEffect(() => {
    if (!formData.uid) return;
    // Only premium/admin users can see visitors
    if (!currentIsPremium && !isAdmin) return;
    const fetchVisitors = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/profile/visitors/${formData.uid}`, { withCredentials: true });
        if (!response.data?.length) return;
        setVisitors(response.data);
        setHasVisitors(true);
      } catch (error) {
        console.error('Error fetching visitors:', error);
      }
    };
    fetchVisitors();
  }, [formData.uid, currentIsPremium, isAdmin]);

  const showEditButton = !uidFromUrl || uidFromUrl === localStorage.getItem('uid');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // console.log('formData', formData.gender);
  };

  const handleDOBChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'dob') {
      const age = calculateAge(value);
      setFormData({ ...formData, dob: value, age });
    }
    console.log('formData', formData);
  };

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleToggleEditMode = () => {
    setEditMode((prevEditMode) => !prevEditMode); // Toggle between edit and view mode
  };


  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!isAdmin && !isPremium) {
  //     window.dispatchEvent(new CustomEvent('goldpetals:open-upgrade', {
  //       detail: { feature: 'Unlimited Profile Edits' }
  //     }));
  //     return;
  //   }
  // };
const handleSubmit = async (e) => {
    e.preventDefault();

    // FREE user → open upgrade modal and stop
    if (!currentIsPremium) {
      setUpgradeModalOpen(true);
      return;
    }

    // PREMIUM user → proceed with actual save
    try {
      const sessionResponse = await axios.get(`${API_BASE_URL}/user/id`, { withCredentials: true });
      const userId = sessionResponse.data?.userId;
      if (!userId) throw new Error('User not authenticated');
      // Parallel save for all modules to ensure end-to-end data persistence
      await Promise.all([
        axios.put(`${API_BASE_URL}/user/update-profile`, formData, { withCredentials: true }),
        axios.post(`${API_BASE_URL}/education`, { educationDetails, user_id: userId }, { withCredentials: true }), // Already present
        axios.post(`${API_BASE_URL}/update-addresses`, { addresses, user_id: userId }, { withCredentials: true }), // Added
        axios.post(`${API_BASE_URL}/familymembers`, { familyMembers, treeId: userId }, { withCredentials: true }), // Added
        axios.post(`${API_BASE_URL}/jobs`, { allJobDetails, user_id: userId }, { withCredentials: true }),
        axios.post(`${API_BASE_URL}/custom_fields`, { CustomField, user_id: userId }, { withCredentials: true })
      ]);

      setOriginalFormData(formData);
      setSuccessDialogOpen(true);
      setEditMode(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError(getErrorMessage(error));
    }
  };

  const [tooltipText, setTooltipText] = useState('Copy Unique Id');

  const handleCopy = () => {
    navigator.clipboard.writeText(formData.uid)
      .then(() => {
        setTooltipText('Unique Id Copied!'); // Update tooltip text
        setTimeout(() => setTooltipText('Copy Unique Id'), 2000); // Reset tooltip after 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  const handleProfilePicUpdate = (newProfilePicUrl) => {
    setProfilePicUrl(getImageUrl(newProfilePicUrl));
    setFormData({ ...formData, image_url: newProfilePicUrl });
  };
  const capitalize = (str) => {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // FIX: education details are now fetched inside fetchUserData above (single session call)
  // Removed separate useEffect that called /user/id independently causing race condition

  const handleFamilyMemberUpdate = (updatedFamilyMembers) => {
    setFamilyMembers(updatedFamilyMembers); // Updates the address state when changes are made in AddressForm
  };

  const handleAddressChange = (updatedAddresses) => {
    setAddresses(updatedAddresses); // Updates the address state when changes are made in AddressForm
  };

  const handleEducationDetails = (updatedDetails) => {
    setEducationDetails(updatedDetails);
  };

  const handleJobDetails = (updatedJobDetails) => {
    setAllJobDetails(updatedJobDetails);
  };

  const handleCustomFieldSave = (updatedCustomField) => {
    setCustomField(updatedCustomField);
  };

  const handleDownloadCard = async () => {
    try {
      const card = cardRef.current;

      // Temporarily make the card visible for capturing
      card.style.display = 'block';

      const canvas = await html2canvas(card, {
        scale: 2,
        backgroundColor: '#ffffff',
      });

      // Hide the card again
      card.style.display = 'none';

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`user-card-${formData.uid}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isGuestUser ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">Please log in to view your profile.</Typography>
        </Box>
      ) : (
        <StyledPaper elevation={0}>
          <WelcomeCard>
            <Stack>
              {/* Welcome Section */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

                <ProfilePictureUpload editMode={editMode} onProfilePicUpdate={handleProfilePicUpdate} userId={userData?.id} />
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight="bold" sx={{
                    background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}>
                    {capitalize(formData.first_name)} {capitalize(formData.last_name)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">

                    <Tooltip title={tooltipText}>
                      <Chip
                        label={formData.uid}
                        variant="outlined"
                        size="small"
                        icon={<ContentCopyIcon />}
                        onClick={handleCopy}
                        sx={{ borderRadius: 1.5 }}
                      />
                    </Tooltip>
                    <Tooltip title="Download User Card">
                      <IconButton
                        onClick={handleDownloadCard}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {showEditButton && (
                  <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                    {currentIsPremium && !isAdmin && (
                      <ActionButton
                        variant="contained"
                        onClick={() => setVisitorDialogOpen(true)}
                        startIcon={<VisibilityIcon />}
                      >
                        Profile Visitors
                      </ActionButton>
                    )}
                  {!currentIsPremium && !isAdmin && (
                      <ActionButton
                        variant="outlined"
                        onClick={() => setUpgradeModalOpen(true)}
                        startIcon={<LockIcon />}
                        endIcon={<VisibilityIcon />}
                      >
                        View Visitors
                      </ActionButton>
                    )}

                    <ActionButton
                      variant="contained"
                      onClick={handleToggleEditMode}
                      startIcon={editMode ? <EditOffIcon /> : <EditIcon />}
                    >
                      {editMode ? 'Disable Edit' : 'Edit Profile'}
                    </ActionButton>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </WelcomeCard>

          {/* Form Sections */}
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 4 }}>
            <Stack spacing={4}>
              {/* Personal Information Section */}
              <Box>
                <SectionTitle>Personal Information</SectionTitle>
                <Stack spacing={3}>
                  {/* Basic Information */}
                  <PersonalInfoCard>
                    <Stack direction="row" alignItems="center" mb={2}>
                      <InfoTypeIcon sx={{
                        ...(editMode ? {
                          color: (theme) => theme.palette.primary.main,
                          opacity: 1
                        } : {
                          color: 'text.secondary',
                          opacity: 0.6
                        })
                      }}>
                        <PersonIcon />
                      </InfoTypeIcon>
                      <Typography variant="h6" fontWeight="bold" sx={{
                        ...(editMode && {
                          background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                        }),
                        ...(!editMode && { color: 'grey' }) // Regular text color when not in edit mode
                      }}>
                        Basic Information
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Prefix"
                          name="prefix"
                          value={formData.prefix || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="First Name"
                          name="first_name"
                          value={formData.first_name || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Middle Name"
                          name="middle_name"
                          value={formData.middle_name || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Last Name"
                          name="last_name"
                          value={formData.last_name || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                    </Grid>
                  </PersonalInfoCard>

                  {/* Display Name */}
                  <PersonalInfoCard>
                    <Stack direction="row" alignItems="center" mb={2}>
                      <InfoTypeIcon sx={{
                        transition: 'all 0.3s ease',
                        ...(editMode ? {
                          color: (theme) => theme.palette.primary.main,
                          opacity: 1
                        } : {
                          color: 'text.disabled',
                          opacity: 0.5
                        })
                      }}>
                        <BadgeIcon />
                      </InfoTypeIcon>
                      <Typography variant="h6" fontWeight="bold" sx={{
                        ...(editMode ? {
                          background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                        } : {
                          color: 'text.secondary',
                          opacity: 0.7
                        })
                      }}>
                        Display Name
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <StyledTextField
                          label="Display Name/ Pet Name"
                          name="display_name"
                          value={formData.display_name || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                    </Grid>
                  </PersonalInfoCard>

                  {/* Contact Information */}
                  <PersonalInfoCard>
                    <Stack direction="row" alignItems="center" mb={2}>
                      <InfoTypeIcon sx={{
                        transition: 'all 0.3s ease',
                        ...(editMode ? {
                          color: (theme) => theme.palette.primary.main,
                          opacity: 1
                        } : {
                          color: 'text.disabled',
                          opacity: 0.5
                        })
                      }}>
                        <ContactPhoneIcon />
                      </InfoTypeIcon>
                      <Typography variant="h6" fontWeight="bold" sx={{
                        ...(editMode ? {
                          background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                        } : {
                          color: 'text.secondary',
                          opacity: 0.7
                        })
                      }}>
                        Contact Information
                      </Typography>

                      <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />

                      <Stack direction="row" spacing={2} alignItems="center">
                        <InfoTypeIcon sx={{
                          transition: 'all 0.3s ease',
                          ...(editMode ? {
                            color: (theme) => theme.palette.primary.main,
                            opacity: 1
                          } : {
                            color: 'text.disabled',
                            opacity: 0.5
                          })
                        }}>
                          <CakeIcon />
                        </InfoTypeIcon>
                        <Typography variant="h6" fontWeight="bold" sx={{
                          ...(editMode ? {
                            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                          } : {
                            color: 'text.secondary',
                            opacity: 0.7
                          })
                        }}>
                          Date of Birth
                        </Typography>
                      </Stack>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Phone Number"
                          name="phone_number"
                          value={formData.phone_number || ''}
                          onChange={handleInputChange}
                          fullWidth
                          inputProps={{
                            maxLength: 10,
                            inputMode: 'numeric',
                          }}
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Email ID"
                          name="email_id"
                          value={formData.email_id || ''}
                          onChange={handleInputChange}
                          fullWidth
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Date of Birth"
                          name="dob"
                          type="date"
                          value={formData.dob || ''}
                          onChange={handleDOBChange}
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                          label="Age"
                          name="age"
                          value={formData.age || ''}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </PersonalInfoCard>


                </Stack>
              </Box>

              {/* Address Section */}
              <Box id="address-details">
                <SectionTitle>Address Details</SectionTitle>
                <Address
                  formData={formData}
                  editMode={editMode}
                  setAllAddressDetails={handleAddressChange}
                />
              </Box>

              {/* Family Details Section */}
              <Box>
                <SectionTitle id="family-details">Family Details</SectionTitle>
                <AddFamilyMember
                  ref={familyMemberRef}
                  formData={formData}
                  editMode={editMode}
                  setAllFamilyMembers={handleFamilyMemberUpdate}
                  villageName={formData.village_name}
                  pinCode={formData.pincode}
                />
              </Box>

              {/* Education Details Section */}
              <Box>
                <SectionTitle>Education Details</SectionTitle>
                <AddEducationDetail
                  formData={formData}
                  editMode={editMode}
                  educationDetails={educationDetails}
                  setAllEducationDetails={setEducationDetails}
                />
              </Box>

              {/* Occupation Details Section */}
              <Box>
                <SectionTitle>Occupation Details</SectionTitle>
                <AddJobDetail
                  formData={formData}
                  editMode={editMode}
                  setAllJobDetails={setAllJobDetails}
                />
              </Box>

              {/* Custom Fields Section */}
              <Box>
                <SectionTitle>Custom Fields</SectionTitle>
                <CustomFieldComponent
                  formData={formData}
                  editMode={editMode}
                  setCustomField={setCustomField}
                />
              </Box>

              {/* Save Button */}
              {editMode && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionButton
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </ActionButton>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Hidden card component for PDF generation */}
          <UserCardForDownload ref={cardRef} userData={formData} />
        </StyledPaper>
      )}
      <SuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      />

      {/* <VisitorDialog
        open={visitorDialogOpen}
        onClose={() => setVisitorDialogOpen(false)}
        visitors={visitors}
      /> */}

<VisitorDialog
        open={visitorDialogOpen}
        onClose={() => setVisitorDialogOpen(false)}
        visitors={visitors}
      />

      {/* <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
      /> */}

<UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onSuccess={handleUpgradeSuccess}
      />
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditProfile;
